import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarPlus,
  faCopy,
  faTrashAlt,
} from "@fortawesome/free-solid-svg-icons";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import { useContentGridCore } from "../../cores/contentGrid";
import { buildPublicContentGridUrl } from "../../cores/contentGrid/services/api";
import { useMeCore } from "../../cores/me";
import { useRouterCore } from "../../cores/router";
import { isInstagramSocialConnected } from "../../cores/socialPost/config";
import { isTiktokPostingEnabled } from "../../cores/config/featureFlags";
import { useSocialContentGridState } from "../../hooks/useSocialContentGridState";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import Layout from "../layout/Layout";
import {
  ContentGridRow,
  createEmptyContentGridRow,
} from "../utils/socialContentGridShare";

const createEmptyRow = (): ContentGridRow => createEmptyContentGridRow();

const statusSelectClass = (status: string) =>
  `form-control-sm social-grid-cell-input social-grid-status-select social-grid-status-select--${status || "pending"}`;

const formatSelectClass = (format: string) =>
  `form-control-sm social-grid-cell-input social-grid-format-select social-grid-format-select--${format || "post"}`;

const mapPlatformToCalendarNetwork = (platform: string): string => {
  const normalized = String(platform || "").trim().toLowerCase();
  if (normalized === "linkedin") return "linkedin";
  if (normalized === "linkedinbusiness") return "linkedinBusiness";
  return normalized;
};

export default function SocialContentGrid() {
  const intl = useIntl();
  const { goToSocialManager, goToSocialCalendar } = useRouterCore();
  const { company, socialSettings } = useMeCore();
  const companyId = useMemo(() => {
    if (company?.id == null || company.id === "") return undefined;
    const parsed = Number(company.id);
    return Number.isFinite(parsed) ? parsed : undefined;
  }, [company?.id]);
  const {
    sendToCalendar,
    createShareLink,
    uploadMedia,
    duplicateRow,
    loading: gridLoading,
  } = useContentGridCore();
  const {
    gridId,
    gridName,
    grids,
    rows,
    setRows,
    hydrated,
    saving,
    inboxMessage,
    setInboxMessage,
    switchGrid,
    createNewGrid,
    duplicateCurrentGrid,
    refreshCurrentGrid,
    syncFromServer,
  } = useSocialContentGridState(company?.id);
  const getGridContext = () => {
    if (companyId === undefined || gridId === null) return null;
    return { companyId, gridId };
  };
  const [isPlatformColumnPinned, setIsPlatformColumnPinned] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [calendarModal, setCalendarModal] = useState<{
    mode: "single" | "bulk";
    rowId?: string;
  } | null>(null);
  const [calendarNetwork, setCalendarNetwork] = useState("instagram");
  const [calendarConfirmModal, setCalendarConfirmModal] = useState<{
    targetIds: string[];
    network: string;
  } | null>(null);
  const [isSendingCalendar, setIsSendingCalendar] = useState(false);
  const [calendarResultMessage, setCalendarResultMessage] = useState<string | null>(
    null
  );
  const [monthFilter, setMonthFilter] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "cards">("grid");
  const [publicLinkMessage, setPublicLinkMessage] = useState<{
    type: "success" | "danger";
    text: string;
  } | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const hasRows = rows.length > 0;
  const availableCalendarNetworks = useMemo(() => {
    const settings = (socialSettings ?? {}) as Record<string, unknown>;
    const options: Array<{ value: string; label: string }> = [];

    if (isInstagramSocialConnected(settings)) {
      options.push({ value: "instagram", label: "Instagram" });
    }
    if (settings.facebook) options.push({ value: "facebook", label: "Facebook" });
    if (settings.linkedIn) options.push({ value: "linkedin", label: "LinkedIn" });
    if (settings.linkedInBusiness) {
      options.push({ value: "linkedinBusiness", label: "LinkedIn Business" });
    }
    if (isTiktokPostingEnabled() && settings.tiktok) {
      options.push({ value: "tiktok", label: "TikTok" });
    }
    if (settings.twitter) options.push({ value: "x", label: "X" });

    return options;
  }, [socialSettings]);

  useEffect(() => {
    if (!hydrated || !companyId || !gridId) return;
    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") {
        refreshCurrentGrid();
      }
    }, 60000);
    return () => window.clearInterval(interval);
  }, [hydrated, companyId, gridId, refreshCurrentGrid]);

  useEffect(() => {
    if (
      availableCalendarNetworks.length > 0 &&
      !availableCalendarNetworks.some((item) => item.value === calendarNetwork)
    ) {
      setCalendarNetwork(availableCalendarNetworks[0].value);
    }
    if (availableCalendarNetworks.length === 0 && calendarNetwork !== "") {
      setCalendarNetwork("");
    }
  }, [availableCalendarNetworks, calendarNetwork]);

  const getNetworkLabel = (networkValue: string) =>
    availableCalendarNetworks.find((item) => item.value === networkValue)?.label ??
    networkValue;

  const filteredRows = useMemo(() => {
    if (!monthFilter) return rows;
    return rows.filter((row) => row.date?.startsWith(monthFilter));
  }, [rows, monthFilter]);
  const filteredRowIds = useMemo(
    () => new Set(filteredRows.map((row) => row.id)),
    [filteredRows]
  );
  const selectedVisibleRowIds = useMemo(
    () => selectedRowIds.filter((id) => filteredRowIds.has(id)),
    [selectedRowIds, filteredRowIds]
  );

  const onChangeRow = (
    id: string,
    field: keyof Omit<ContentGridRow, "id">,
    value: string
  ) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const renderMediaPreview = (row: ContentGridRow) => {
    if (!row.mediaUrl) return null;

    if (row.mediaType === "image") {
      return (
        <img
          src={row.mediaUrl}
          alt="media-preview"
          className="social-grid-media-preview social-grid-media-preview--image"
        />
      );
    }

    if (row.mediaType === "video") {
      return (
        <video
          src={row.mediaUrl}
          controls
          className="social-grid-media-preview social-grid-media-preview--video"
        />
      );
    }

    if (row.mediaType === "link") {
      return (
        <a
          href={row.mediaUrl}
          target="_blank"
          rel="noreferrer"
          className="social-grid-media-link"
        >
          {intl.formatMessage({ id: "app.socialContentGrid.openLink" })}
        </a>
      );
    }

    return null;
  };

  const renderRowActions = (row: ContentGridRow) => (
    <div className="social-grid-row-actions social-grid-row-actions--compact">
      <Button
        color="danger"
        outline
        size="sm"
        className="social-grid-icon-btn"
        title={intl.formatMessage({ id: "app.socialContentGrid.delete" })}
        aria-label={intl.formatMessage({ id: "app.socialContentGrid.delete" })}
        onClick={() => removeRow(row.id)}
      >
        <FontAwesomeIcon icon={faTrashAlt} />
      </Button>
      <Button
        color="secondary"
        outline
        size="sm"
        className="social-grid-icon-btn"
        title={intl.formatMessage({
          id: "app.socialContentGrid.duplicateRow",
          defaultMessage: "Duplicar",
        })}
        aria-label={intl.formatMessage({
          id: "app.socialContentGrid.duplicateRow",
          defaultMessage: "Duplicar",
        })}
        onClick={() => duplicateGridRow(row.id)}
      >
        <FontAwesomeIcon icon={faCopy} />
      </Button>
      <Button
        color="primary"
        size="sm"
        className="social-grid-icon-btn"
        title={intl.formatMessage({ id: "app.socialContentGrid.sendToCalendar" })}
        aria-label={intl.formatMessage({ id: "app.socialContentGrid.sendToCalendar" })}
        onClick={() => openSingleCalendarModal(row.id)}
        disabled={row.scheduledToCalendar}
      >
        <FontAwesomeIcon icon={faCalendarPlus} />
      </Button>
    </div>
  );

  const onUploadMedia = async (
    id: string,
    file: File,
    mediaType: ContentGridRow["mediaType"]
  ) => {
    const ctx = getGridContext();
    if (!ctx || mediaType !== "image" && mediaType !== "video") return;
    const updated = await uploadMedia(ctx.gridId, id, ctx.companyId, file, mediaType);
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...updated } : row))
    );
  };

  const duplicateGridRow = async (rowId: string) => {
    const ctx = getGridContext();
    if (!ctx) return;
    const grid = await duplicateRow(ctx.gridId, rowId, ctx.companyId);
    syncFromServer(grid);
  };

  const handleRefreshGrid = async () => {
    setIsRefreshing(true);
    try {
      await refreshCurrentGrid();
    } finally {
      setIsRefreshing(false);
    }
  };

  const addRow = () => {
    setRows((prev) => [...prev, createEmptyRow()]);
  };

  const removeRow = (id: string) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
    setSelectedRowIds((prev) => prev.filter((rowId) => rowId !== id));
  };

  const toggleRowSelection = (rowId: string) => {
    setSelectedRowIds((prev) =>
      prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]
    );
  };

  const openSingleCalendarModal = (rowId: string) => {
    const row = rows.find((item) => item.id === rowId);
    if (row?.platform) {
      const preferred = mapPlatformToCalendarNetwork(row.platform);
      if (availableCalendarNetworks.some((item) => item.value === preferred)) {
        setCalendarNetwork(preferred);
      }
    }
    setCalendarModal({ mode: "single", rowId });
  };

  const getTargetIdsFromModal = () => {
    if (!calendarModal) return [] as string[];
    return calendarModal.mode === "single" && calendarModal.rowId
      ? [calendarModal.rowId]
      : selectedVisibleRowIds;
  };

  const openBulkCalendarModal = () => {
    if (selectedVisibleRowIds.length === 0) return;
    setCalendarModal({ mode: "bulk" });
  };

  const confirmSendToCalendar = async () => {
    if (!calendarConfirmModal) return;
    const ctx = getGridContext();
    if (!ctx) {
      setCalendarResultMessage(
        intl.formatMessage({
          id: "app.socialContentGrid.calendarSendMissingCompany",
          defaultMessage: "No se encontro la compania activa para programar publicaciones.",
        })
      );
      setCalendarConfirmModal(null);
      return;
    }
    const { targetIds, network } = calendarConfirmModal;
    if (targetIds.length === 0) {
      setCalendarConfirmModal(null);
      return;
    }

    setIsSendingCalendar(true);
    try {
      const result = await sendToCalendar(ctx.gridId, ctx.companyId, targetIds, network);
      syncFromServer(result.grid);

      const failures = result.results.filter((item) => item.error);
      const successes = result.results.filter((item) => item.socialPostId);

      if (failures.length > 0 && successes.length > 0) {
        setCalendarResultMessage(
          intl.formatMessage(
            {
              id: "app.socialContentGrid.calendarSendPartial",
              defaultMessage:
                "Se programaron {ok} publicaciones. {fail} no se pudieron enviar.",
            },
            { ok: successes.length, fail: failures.length }
          )
        );
      } else if (failures.length > 0) {
        setCalendarResultMessage(
          intl.formatMessage({
            id: "app.socialContentGrid.calendarSendFailed",
            defaultMessage: "No se pudieron enviar las publicaciones al calendario.",
          })
        );
      } else {
        setCalendarResultMessage(
          intl.formatMessage(
            {
              id: "app.socialContentGrid.calendarSendSuccess",
              defaultMessage: "Se enviaron {count} publicaciones al calendario.",
            },
            { count: successes.length || targetIds.length }
          )
        );
      }

      if (calendarModal?.mode === "bulk") {
        setSelectedRowIds([]);
      }
    } finally {
      setIsSendingCalendar(false);
      setCalendarConfirmModal(null);
      setCalendarModal(null);
    }
  };

  const copyPublicLink = async () => {
    const ctx = getGridContext();
    if (!ctx) return;

    try {
      const { token } = await createShareLink(ctx.gridId, ctx.companyId);
      const url = buildPublicContentGridUrl(token);
      await navigator.clipboard.writeText(url);
      setPublicLinkMessage({
        type: "success",
        text: intl.formatMessage({
          id: "app.socialContentGrid.publicLinkCopied",
          defaultMessage: "Link publico generado y copiado correctamente.",
        }),
      });
    } catch (error) {
      setPublicLinkMessage({
        type: "danger",
        text: intl.formatMessage({
          id: "app.socialContentGrid.publicLinkCopyError",
          defaultMessage:
            "No se pudo copiar automaticamente. Intenta nuevamente.",
        }),
      });
    }
  };

  return (
    <Layout>
      <Header2
        className="social-grid-header"
        title={intl.formatMessage({ id: "app.sidebar.socialContentGrid" })}
      />
      <Content>
        <Container fluid className="social-content-grid-page">
          {inboxMessage ? (
            <Row>
              <Col xs="12">
                <div className="alert alert-success py-2" role="status">
                  {inboxMessage}
                  <button
                    type="button"
                    className="close"
                    aria-label="Close"
                    onClick={() => setInboxMessage(null)}
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
              </Col>
            </Row>
          ) : null}
          <Row>
            <Col xs="12">
              <Card className="border-0 shadow-sm mb-3">
                <CardBody className="social-grid-toolbar d-flex flex-wrap gap-2 align-items-center justify-content-between">
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    {grids.length > 0 ? (
                      <Input
                        type="select"
                        value={gridId ?? ""}
                        onChange={(e) => switchGrid(Number(e.target.value))}
                        className="rounded"
                        style={{ minWidth: 220 }}
                        disabled={gridLoading || !gridId}
                      >
                        {grids.map((grid) => (
                          <option key={grid.id} value={grid.id}>
                            {grid.name}
                          </option>
                        ))}
                      </Input>
                    ) : (
                      <span className="text-muted">{gridName || "Grilla de contenidos"}</span>
                    )}
                    <Button color="secondary" outline onClick={createNewGrid} disabled={gridLoading}>
                      {intl.formatMessage({
                        id: "app.socialContentGrid.newGrid",
                        defaultMessage: "Nueva grilla",
                      })}
                    </Button>
                    <Button
                      color="secondary"
                      outline
                      onClick={duplicateCurrentGrid}
                      disabled={gridLoading || !gridId}
                    >
                      {intl.formatMessage({
                        id: "app.socialContentGrid.duplicateGrid",
                        defaultMessage: "Duplicar grilla",
                      })}
                    </Button>
                    <Button
                      color="info"
                      outline
                      onClick={handleRefreshGrid}
                      disabled={isRefreshing || !gridId}
                    >
                      {intl.formatMessage({
                        id: "app.socialContentGrid.refresh",
                        defaultMessage: "Actualizar",
                      })}
                    </Button>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {saving ? (
                      <Badge color="warning" pill className="px-3 py-2">
                        {intl.formatMessage({
                          id: "app.socialContentGrid.saving",
                          defaultMessage: "Guardando...",
                        })}
                      </Badge>
                    ) : hydrated ? (
                      <Badge color="success" pill className="px-3 py-2">
                        {intl.formatMessage({
                          id: "app.socialContentGrid.saved",
                          defaultMessage: "Guardado",
                        })}
                      </Badge>
                    ) : null}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col xs="12">
              <Card className="border-0 shadow-sm mb-3">
                <CardBody className="social-grid-toolbar d-flex flex-wrap gap-2 align-items-center justify-content-between">
                  <div className="social-grid-toolbar-actions d-flex flex-wrap gap-2 align-items-center">
                    <Button color="primary" onClick={addRow}>
                      {intl.formatMessage({ id: "app.socialContentGrid.addRow" })}
                    </Button>
                    <Button
                      color="success"
                      onClick={copyPublicLink}
                      disabled={!hasRows || !gridId}
                    >
                      {intl.formatMessage({
                        id: "app.socialContentGrid.generatePublicLink",
                      })}
                    </Button>
                    <Button
                      color={isPlatformColumnPinned ? "secondary" : "info"}
                      outline
                      className="social-grid-pin-btn rounded-pill"
                      onClick={() => setIsPlatformColumnPinned((prev) => !prev)}
                    >
                      {isPlatformColumnPinned
                        ? intl.formatMessage({
                            id: "app.socialContentGrid.unpinSocialColumn",
                            defaultMessage: "Ocultar columna Red Social",
                          })
                        : intl.formatMessage({
                            id: "app.socialContentGrid.pinSocialColumn",
                            defaultMessage: "Fijar columna Red Social",
                          })}
                    </Button>
                  </div>
                  <Badge color="light" pill className="px-3 py-2 border">
                    {rows.length} items
                  </Badge>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col xs="12">
              <Card className="border-0 shadow-sm mb-3">
                <CardBody className="social-grid-toolbar d-flex flex-wrap gap-2 align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2">
                    <Badge color="info" pill className="px-3 py-2">
                    {selectedVisibleRowIds.length}
                    </Badge>
                    <span>
                      {intl.formatMessage({ id: "app.socialContentGrid.selectedRows" })}
                    </span>
                  </div>
                  <Button
                    color="primary"
                    className="rounded-pill"
                    onClick={openBulkCalendarModal}
                    disabled={selectedVisibleRowIds.length === 0}
                  >
                    {intl.formatMessage({
                      id: "app.socialContentGrid.sendSelectedToCalendar",
                    })}
                  </Button>
                </CardBody>
              </Card>
              <Card className="border-0 shadow-sm mb-3">
                <CardBody className="social-grid-toolbar d-flex flex-wrap gap-2 align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2">
                    <label className="mb-0">
                      {intl.formatMessage({
                        id: "app.socialContentGrid.filterByMonth",
                        defaultMessage: "Filtrar por mes",
                      })}
                    </label>
                    <Input
                      type="month"
                      value={monthFilter}
                      onChange={(e) => setMonthFilter(e.target.value)}
                      onClick={(e) => (e.currentTarget as HTMLInputElement).showPicker?.()}
                      className="rounded"
                    />
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      color={viewMode === "grid" ? "primary" : "secondary"}
                      outline={viewMode !== "grid"}
                      className="rounded-pill"
                      onClick={() => setViewMode("grid")}
                    >
                      {intl.formatMessage({
                        id: "app.socialContentGrid.view.grid",
                        defaultMessage: "Grilla",
                      })}
                    </Button>
                    <Button
                      color={viewMode === "cards" ? "primary" : "secondary"}
                      outline={viewMode !== "cards"}
                      className="rounded-pill"
                      onClick={() => setViewMode("cards")}
                    >
                      {intl.formatMessage({
                        id: "app.socialContentGrid.view.cards",
                        defaultMessage: "Cards",
                      })}
                    </Button>
                    <Button
                      color="secondary"
                      outline
                      className="rounded-pill"
                      onClick={() => setMonthFilter("")}
                      disabled={!monthFilter}
                    >
                      {intl.formatMessage({
                        id: "app.socialContentGrid.allMonths",
                        defaultMessage: "Todos los meses",
                      })}
                    </Button>
                  </div>
                </CardBody>
              </Card>
              {viewMode === "grid" ? (
                <Card className="border-0 shadow-sm social-grid-table-card">
                  <CardBody className="p-0">
                    <div className="table-responsive social-grid-table-wrap">
                      <Table hover className="mb-0 public-grid-table social-grid-admin-table">
                        <thead>
                          <tr>
                            <th className="social-grid-select-col" title={intl.formatMessage({ id: "app.socialContentGrid.select" })}>
                              <span className="social-grid-th-short">✓</span>
                            </th>
                            <th className="social-grid-col-date">{intl.formatMessage({ id: "app.socialContentGrid.date" })}</th>
                            <th className="social-grid-col-time">{intl.formatMessage({ id: "app.socialContentGrid.time", defaultMessage: "Hora" })}</th>
                            {isPlatformColumnPinned && (
                              <th className="social-grid-col-platform">{intl.formatMessage({ id: "app.socialContentGrid.platform" })}</th>
                            )}
                            <th className="social-grid-col-format">{intl.formatMessage({ id: "app.socialContentGrid.format" })}</th>
                            <th className="social-grid-col-media">{intl.formatMessage({ id: "app.socialContentGrid.media" })}</th>
                            <th className="social-grid-col-copy">{intl.formatMessage({ id: "app.socialContentGrid.copy" })}</th>
                            <th className="social-grid-col-comments">{intl.formatMessage({ id: "app.socialContentGrid.comments" })}</th>
                            <th className="social-grid-col-status">{intl.formatMessage({ id: "app.socialContentGrid.status" })}</th>
                            <th className="social-grid-col-calendar">{intl.formatMessage({ id: "app.socialContentGrid.calendar" })}</th>
                            <th className="social-grid-col-actions">{intl.formatMessage({ id: "app.socialContentGrid.actions" })}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredRows.map((row) => (
                            <tr key={row.id} className="social-grid-admin-row">
                              <td className="social-grid-select-col">
                                <Input
                                  type="checkbox"
                                  className="social-grid-checkbox"
                                  checked={selectedRowIds.includes(row.id)}
                                  onChange={() => toggleRowSelection(row.id)}
                                  disabled={row.scheduledToCalendar}
                                />
                              </td>
                              <td className="social-grid-col-date">
                                <Input
                                  type="date"
                                  value={row.date}
                                  onChange={(e) => onChangeRow(row.id, "date", e.target.value)}
                                  className="form-control-sm social-grid-cell-input"
                                />
                              </td>
                              <td className="social-grid-col-time">
                                <Input
                                  type="time"
                                  value={row.time || "12:00"}
                                  onChange={(e) => onChangeRow(row.id, "time", e.target.value)}
                                  className="form-control-sm social-grid-cell-input"
                                />
                              </td>
                              {isPlatformColumnPinned && (
                                <td className="social-grid-col-platform">
                                  <Input
                                    type="select"
                                    value={row.platform}
                                    onChange={(e) => onChangeRow(row.id, "platform", e.target.value)}
                                    className="form-control-sm social-grid-cell-input"
                                  >
                                    <option value="instagram">Instagram</option>
                                    <option value="facebook">Facebook</option>
                                    <option value="linkedin">LinkedIn</option>
                                    {isTiktokPostingEnabled() ? (
                                      <option value="tiktok">TikTok</option>
                                    ) : null}
                                    <option value="x">X</option>
                                    <option value="youtube">YouTube</option>
                                  </Input>
                                </td>
                              )}
                              <td className="social-grid-col-format">
                                <Input
                                  type="select"
                                  value={row.format}
                                  onChange={(e) => onChangeRow(row.id, "format", e.target.value)}
                                  className={formatSelectClass(row.format)}
                                >
                                  <option value="post">Post</option>
                                  <option value="story">Story</option>
                                  <option value="reel">Reel</option>
                                  <option value="video">Video</option>
                                  <option value="carousel">Carousel</option>
                                </Input>
                              </td>
                              <td className="social-grid-col-media">
                                <div className="social-grid-media-cell">
                                  <Input
                                    type="select"
                                    value={row.mediaType}
                                    onChange={(e) => {
                                      const mediaType = e.target.value as ContentGridRow["mediaType"];
                                      onChangeRow(row.id, "mediaType", mediaType);
                                      if (mediaType === "none") onChangeRow(row.id, "mediaUrl", "");
                                    }}
                                    className="form-control-sm social-grid-cell-input"
                                  >
                                    <option value="none">{intl.formatMessage({ id: "app.socialContentGrid.mediaType.none" })}</option>
                                    <option value="image">{intl.formatMessage({ id: "app.socialContentGrid.mediaType.image" })}</option>
                                    <option value="video">{intl.formatMessage({ id: "app.socialContentGrid.mediaType.video" })}</option>
                                    <option value="link">{intl.formatMessage({ id: "app.socialContentGrid.mediaType.link" })}</option>
                                  </Input>
                                  {row.mediaType === "link" ? (
                                    <Input
                                      className="form-control-sm social-grid-cell-input mt-1"
                                      value={row.mediaUrl}
                                      placeholder={intl.formatMessage({
                                        id: "app.socialContentGrid.linkPlaceholder",
                                      })}
                                      onChange={(e) =>
                                        onChangeRow(row.id, "mediaUrl", e.target.value)
                                      }
                                    />
                                  ) : row.mediaType === "image" ||
                                    row.mediaType === "video" ? (
                                    <Input
                                      className="form-control-sm social-grid-cell-input social-grid-file-input mt-1"
                                      type="file"
                                      accept={
                                        row.mediaType === "image" ? "image/*" : "video/*"
                                      }
                                      onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        await onUploadMedia(row.id, file, row.mediaType);
                                      }}
                                    />
                                  ) : null}
                                  {renderMediaPreview(row)}
                                </div>
                              </td>
                              <td className="social-grid-col-copy">
                                <Input
                                  type="textarea"
                                  rows={2}
                                  value={row.copy}
                                  onChange={(e) => onChangeRow(row.id, "copy", e.target.value)}
                                  className="form-control-sm social-grid-cell-input social-grid-textarea"
                                  placeholder={intl.formatMessage({
                                    id: "app.socialContentGrid.copyPlaceholder",
                                    defaultMessage: "Escribe el copy del post...",
                                  })}
                                />
                              </td>
                              <td className="social-grid-col-comments">
                                <Input
                                  type="textarea"
                                  rows={2}
                                  value={row.comments}
                                  onChange={(e) => onChangeRow(row.id, "comments", e.target.value)}
                                  className="form-control-sm social-grid-cell-input social-grid-textarea"
                                  placeholder={intl.formatMessage({
                                    id: "app.socialContentGrid.commentsPlaceholder",
                                    defaultMessage: "Notas internas o feedback...",
                                  })}
                                />
                              </td>
                              <td className="social-grid-col-status">
                                <Input
                                  type="select"
                                  value={row.status}
                                  onChange={(e) => onChangeRow(row.id, "status", e.target.value)}
                                  className={statusSelectClass(row.status)}
                                >
                                  <option value="pending">{intl.formatMessage({ id: "app.socialContentGrid.status.pending" })}</option>
                                  <option value="approved">{intl.formatMessage({ id: "app.socialContentGrid.status.approved" })}</option>
                                  <option value="rejected">{intl.formatMessage({ id: "app.socialContentGrid.status.rejected" })}</option>
                                </Input>
                              </td>
                              <td className="social-grid-col-calendar">
                                {row.scheduledToCalendar ? (
                                  <div className="social-grid-calendar-badge">
                                    <Badge color="success" pill className="social-grid-scheduled-badge">
                                      {row.scheduledNetwork || "-"}
                                    </Badge>
                                    {row.socialPostId ? (
                                      <Button
                                        color="link"
                                        size="sm"
                                        className="social-grid-calendar-link p-0"
                                        onClick={goToSocialCalendar}
                                      >
                                        {intl.formatMessage({
                                          id: "app.socialContentGrid.viewInCalendar",
                                          defaultMessage: "Ver en calendario",
                                        })}
                                      </Button>
                                    ) : null}
                                  </div>
                                ) : (
                                  <Badge color="light" pill className="social-grid-unscheduled-badge">
                                    {intl.formatMessage({ id: "app.socialContentGrid.notScheduled" })}
                                  </Badge>
                                )}
                              </td>
                              <td className="social-grid-col-actions">
                                {renderRowActions(row)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              ) : (
                <div className="social-grid-cards social-grid-cards--cards">
                  {filteredRows.map((row, index) => (
                    <Card key={row.id} className="border-0 shadow-sm social-grid-item-card">
                    <CardHeader className="social-grid-item-card__header d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-2">
                        <Input
                          type="checkbox"
                          className="social-grid-checkbox"
                          checked={selectedRowIds.includes(row.id)}
                          onChange={() => toggleRowSelection(row.id)}
                          disabled={row.scheduledToCalendar}
                        />
                        <strong>{`#${index + 1}`}</strong>
                      </div>
                      {row.scheduledToCalendar ? (
                        <Badge color="success" pill className="px-2 py-1">
                          {row.scheduledNetwork || "-"}
                        </Badge>
                      ) : (
                        <Badge color="secondary" pill className="px-2 py-1">
                          {intl.formatMessage({ id: "app.socialContentGrid.notScheduled" })}
                        </Badge>
                      )}
                    </CardHeader>
                    <CardBody>
                      <div className="social-grid-item-card__body">
                        <div>
                          <label>{intl.formatMessage({ id: "app.socialContentGrid.date" })}</label>
                          <Input
                            type="date"
                            value={row.date}
                            onChange={(e) => onChangeRow(row.id, "date", e.target.value)}
                            className="rounded"
                          />
                        </div>
                        <div>
                          <label>{intl.formatMessage({ id: "app.socialContentGrid.time", defaultMessage: "Hora" })}</label>
                          <Input
                            type="time"
                            value={row.time || "12:00"}
                            onChange={(e) => onChangeRow(row.id, "time", e.target.value)}
                            className="rounded"
                          />
                        </div>
                        {isPlatformColumnPinned && (
                          <div>
                            <label>{intl.formatMessage({ id: "app.socialContentGrid.platform" })}</label>
                            <Input
                              type="select"
                              value={row.platform}
                              onChange={(e) => onChangeRow(row.id, "platform", e.target.value)}
                              className="rounded"
                            >
                              <option value="instagram">Instagram</option>
                              <option value="facebook">Facebook</option>
                              <option value="linkedin">LinkedIn</option>
                              {isTiktokPostingEnabled() ? (
                                <option value="tiktok">TikTok</option>
                              ) : null}
                              <option value="x">X</option>
                              <option value="youtube">YouTube</option>
                            </Input>
                          </div>
                        )}
                        <div>
                          <label>{intl.formatMessage({ id: "app.socialContentGrid.format" })}</label>
                          <Input
                            type="select"
                            value={row.format}
                            onChange={(e) => onChangeRow(row.id, "format", e.target.value)}
                            className="rounded"
                          >
                            <option value="post">Post</option>
                            <option value="story">Story</option>
                            <option value="reel">Reel</option>
                            <option value="video">Video</option>
                            <option value="carousel">Carousel</option>
                          </Input>
                        </div>
                        <div className="social-grid-card-field--wide">
                          <label>{intl.formatMessage({ id: "app.socialContentGrid.media" })}</label>
                          <Input
                            type="select"
                            value={row.mediaType}
                            onChange={(e) => {
                              const mediaType = e.target.value as ContentGridRow["mediaType"];
                              onChangeRow(row.id, "mediaType", mediaType);
                              if (mediaType === "none") {
                                onChangeRow(row.id, "mediaUrl", "");
                              }
                            }}
                            className="rounded"
                          >
                            <option value="none">{intl.formatMessage({ id: "app.socialContentGrid.mediaType.none" })}</option>
                            <option value="image">{intl.formatMessage({ id: "app.socialContentGrid.mediaType.image" })}</option>
                            <option value="video">{intl.formatMessage({ id: "app.socialContentGrid.mediaType.video" })}</option>
                            <option value="link">{intl.formatMessage({ id: "app.socialContentGrid.mediaType.link" })}</option>
                          </Input>
                          {row.mediaType === "link" ? (
                            <Input
                              className="mt-2 rounded"
                              value={row.mediaUrl}
                              placeholder={intl.formatMessage({
                                id: "app.socialContentGrid.linkPlaceholder",
                              })}
                              onChange={(e) => onChangeRow(row.id, "mediaUrl", e.target.value)}
                            />
                          ) : row.mediaType === "image" || row.mediaType === "video" ? (
                            <Input
                              className="mt-2 rounded"
                              type="file"
                              accept={row.mediaType === "image" ? "image/*" : "video/*"}
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                await onUploadMedia(row.id, file, row.mediaType);
                              }}
                            />
                          ) : null}
                          {renderMediaPreview(row)}
                        </div>
                        <div className="social-grid-card-field--wide">
                          <label>{intl.formatMessage({ id: "app.socialContentGrid.copy" })}</label>
                          <Input
                            type="textarea"
                            rows={3}
                            value={row.copy}
                            onChange={(e) => onChangeRow(row.id, "copy", e.target.value)}
                            className="rounded"
                          />
                        </div>
                        <div className="social-grid-card-field--wide">
                          <label>{intl.formatMessage({ id: "app.socialContentGrid.comments" })}</label>
                          <Input
                            type="textarea"
                            rows={3}
                            value={row.comments}
                            onChange={(e) => onChangeRow(row.id, "comments", e.target.value)}
                            className="rounded"
                          />
                        </div>
                        <div>
                          <label>{intl.formatMessage({ id: "app.socialContentGrid.status" })}</label>
                          <Input
                            type="select"
                            value={row.status}
                            onChange={(e) => onChangeRow(row.id, "status", e.target.value)}
                            className="rounded"
                          >
                            <option value="pending">{intl.formatMessage({ id: "app.socialContentGrid.status.pending" })}</option>
                            <option value="approved">{intl.formatMessage({ id: "app.socialContentGrid.status.approved" })}</option>
                            <option value="rejected">{intl.formatMessage({ id: "app.socialContentGrid.status.rejected" })}</option>
                          </Input>
                        </div>
                      </div>
                      <div className="social-grid-row-actions d-flex gap-2 flex-wrap mt-3">
                        {renderRowActions(row)}
                      </div>
                    </CardBody>
                  </Card>
                  ))}
                </div>
              )}
              {filteredRows.length === 0 && (
                <Card className="border-0 shadow-sm mt-3">
                  <CardBody className="text-center py-4">
                    {intl.formatMessage({
                      id: "app.socialContentGrid.emptyFiltered",
                      defaultMessage: "No hay contenidos para el mes seleccionado.",
                    })}
                  </CardBody>
                </Card>
              )}
            </Col>
          </Row>
          <Modal
            isOpen={Boolean(calendarModal)}
            toggle={() => setCalendarModal(null)}
            className="social-grid-calendar-modal"
            contentClassName="social-grid-calendar-modal__content"
            centered
          >
            <ModalHeader toggle={() => setCalendarModal(null)}>
              {intl.formatMessage({ id: "app.socialContentGrid.calendarModalTitle" })}
            </ModalHeader>
            <ModalBody>
              <p className="mb-3 social-grid-calendar-modal__description">
                {intl.formatMessage({
                  id:
                    calendarModal?.mode === "bulk"
                      ? "app.socialContentGrid.calendarBulkDescription"
                      : "app.socialContentGrid.calendarSingleDescription",
                })}
              </p>
              <label className="mb-2 d-block font-weight-bold">
                {intl.formatMessage({
                  id: "app.socialContentGrid.selectNetwork",
                  defaultMessage: "Selecciona la red social destino",
                })}
              </label>
              {availableCalendarNetworks.length > 0 ? (
                <Input
                  type="select"
                  value={calendarNetwork}
                  onChange={(e) => setCalendarNetwork(e.target.value)}
                  className="rounded"
                >
                  {availableCalendarNetworks.map((network) => (
                    <option key={network.value} value={network.value}>
                      {network.label}
                    </option>
                  ))}
                </Input>
              ) : (
                <div>
                  <p className="mb-2 text-warning">
                    {intl.formatMessage({
                      id: "app.socialContentGrid.noSyncedNetworks",
                      defaultMessage:
                        "No hay redes sociales sincronizadas para enviar al calendario.",
                    })}
                  </p>
                  <Button color="info" outline onClick={goToSocialManager}>
                    {intl.formatMessage({
                      id: "app.socialContentGrid.syncNetworksCta",
                      defaultMessage: "Ir a Redes Sociales para sincronizar",
                    })}
                  </Button>
                  <p className="mt-2 mb-0 text-muted">
                    {intl.formatMessage({
                      id: "app.socialContentGrid.syncNetworksHint",
                      defaultMessage:
                        "Necesitas al menos una red conectada para programar contenidos en el calendario.",
                    })}
                  </p>
                </div>
              )}
            </ModalBody>
            <ModalFooter className="social-grid-calendar-modal__footer">
              <Button color="secondary" onClick={() => setCalendarModal(null)}>
                {intl.formatMessage({ id: "app.socialContentGrid.cancel" })}
              </Button>
              <Button
                color="primary"
                onClick={() => {
                  if (!calendarNetwork) return;
                  const targetIds = getTargetIdsFromModal();
                  if (targetIds.length === 0) return;
                  setCalendarConfirmModal({
                    targetIds,
                    network: calendarNetwork,
                  });
                }}
                disabled={availableCalendarNetworks.length === 0 || !calendarNetwork}
              >
                {intl.formatMessage({ id: "app.socialContentGrid.confirmSendCalendar" })}
              </Button>
            </ModalFooter>
          </Modal>
          <Modal
            isOpen={Boolean(calendarConfirmModal)}
            toggle={() => setCalendarConfirmModal(null)}
            className="social-grid-calendar-modal"
            contentClassName="social-grid-calendar-modal__content"
            centered
          >
            <ModalHeader toggle={() => setCalendarConfirmModal(null)}>
              {intl.formatMessage({
                id: "app.socialContentGrid.confirmDecisionTitle",
                defaultMessage: "Confirmar decisión",
              })}
            </ModalHeader>
            <ModalBody>
              {intl.formatMessage(
                {
                  id: "app.socialContentGrid.confirmCalendarDispatchDescription",
                  defaultMessage:
                    "Vas a enviar {count} publicaciones al calendario para {network}. ¿Deseas continuar?",
                },
                {
                  count: calendarConfirmModal?.targetIds.length ?? 0,
                  network: getNetworkLabel(
                    calendarConfirmModal?.network ?? calendarNetwork
                  ),
                }
              )}
            </ModalBody>
            <ModalFooter className="social-grid-calendar-modal__footer">
              <Button
                color="secondary"
                onClick={() => setCalendarConfirmModal(null)}
                disabled={isSendingCalendar}
              >
                {intl.formatMessage({ id: "app.socialContentGrid.cancel" })}
              </Button>
              <Button
                color="primary"
                onClick={confirmSendToCalendar}
                disabled={isSendingCalendar}
              >
                {isSendingCalendar
                  ? intl.formatMessage({
                      id: "app.asiggnUser.searching",
                      defaultMessage: "Procesando...",
                    })
                  : intl.formatMessage({
                      id: "app.socialContentGrid.confirmAndSend",
                    })}
              </Button>
            </ModalFooter>
          </Modal>
          <Modal
            isOpen={Boolean(calendarResultMessage)}
            toggle={() => setCalendarResultMessage(null)}
            centered
          >
            <ModalHeader toggle={() => setCalendarResultMessage(null)}>
              {intl.formatMessage({
                id: "app.socialContentGrid.calendarModalTitle",
                defaultMessage: "Calendario",
              })}
            </ModalHeader>
            <ModalBody>{calendarResultMessage}</ModalBody>
            <ModalFooter>
              <Button color="primary" onClick={() => setCalendarResultMessage(null)}>
                {intl.formatMessage({
                  id: "app.socialContentGrid.accept",
                  defaultMessage: "Aceptar",
                })}
              </Button>
            </ModalFooter>
          </Modal>
          <Modal
            isOpen={Boolean(publicLinkMessage)}
            toggle={() => setPublicLinkMessage(null)}
            centered
          >
            <ModalHeader toggle={() => setPublicLinkMessage(null)}>
              {intl.formatMessage({
                id: "app.socialContentGrid.publicLinkModalTitle",
                defaultMessage: "Link publico",
              })}
            </ModalHeader>
            <ModalBody>
              {publicLinkMessage?.text}
            </ModalBody>
            <ModalFooter>
              <Button
                color={publicLinkMessage?.type === "success" ? "success" : "secondary"}
                onClick={() => setPublicLinkMessage(null)}
              >
                {intl.formatMessage({
                  id: "app.socialContentGrid.accept",
                  defaultMessage: "Aceptar",
                })}
              </Button>
            </ModalFooter>
          </Modal>
        </Container>
      </Content>
    </Layout>
  );
}
